import React from 'react';
import type { CSSProperties } from 'react';
import { observer } from 'mobx-react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';

import { TYPES } from '@/engine/ioc';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import { SplineItems, type ISplineDetails } from '@/engine/app/interfaces.ts';

import { EyeCheckboxButton, PenCheckboxButton } from '../../CheckboxButton.tsx';
import { useInjection } from 'inversify-react';
import { StyledTableCell, UpdatableResetIconButton } from '../../BoxTools.tsx';
import { useHighlight } from '../../hooks/useHighlight.ts';
import { useSelect } from '../../hooks/useSelect.ts';
import { UpdatableValue } from '../../common/UpdatableValue.tsx';
import { getConditionalStyles } from '../../styles.ts';
import { getSplineItemStyleConfig, WamDescriptor } from './WideAreaMeasurementConfigGetter.ts';

import type {
  ISplineItemConfig,
  IWideAreaMeasurementConfigStrategy,
  SplineAreaMeasurementBoxProps
} from './interface.ts';
import type { MeasurementProps } from '../types.ts';
import type { MeasurementBoxesController } from '../index.ts';

export const WideAreaMeasurementBox: React.FC<SplineAreaMeasurementBoxProps> = observer((props) => {
  const theme = useTheme();

  const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const configStrategy = useInjection<IWideAreaMeasurementConfigStrategy>(TYPES.WideAreaMeasurementConfigStrategy);
  const measurementVMs = viewsService.currentView?.getMeasurements();

  const { isHighlighted } = useHighlight(measurementVMs);
  const { isSelected } = useSelect(measurementVMs);

  return (
    <TableContainer component={Paper}>
      {props.measurements.map((measurement: MeasurementProps, index) => {
        const config = configStrategy.getConfigByMeasurementSceneItemKind(measurement.kind);

        if (!config) throw new Error('Config not found');

        const isItemSelected: boolean = isSelected(measurement.descriptor);
        const isItemHighlighted: boolean = isHighlighted(measurement.descriptor);

        const splineDetails = measurementVMs?.find((ms) => ms.descriptor === measurement.descriptor)
          ?.details as ISplineDetails;

        return (
          <React.Fragment key={'fragment-' + index}>
            <Table aria-label="a dense table" size="small" key={'wideareaTable' + index}>
              <TableBody
                key={'wideareaHeader' + index}
                sx={{
                  '& td:first-child svg': {
                    fontSize: '1em',
                    marginRight: '0.3em',
                    opacity: 0.7
                  }
                }}
                onClick={(): void => {
                  mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
                }}
              >
                <TableRow
                  aria-roledescription={'landing zone header'}
                  key={measurement.label + index}
                  data-testid={measurement.descriptor + 'Area'}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '& td:first-child svg': {
                      fontSize: '1em',
                      marginRight: '0.3em'
                    },
                    ...getConditionalStyles(
                      isItemHighlighted,
                      theme,
                      {
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingRight: 0.1
                      },
                      {
                        paddingRight: 0.5,
                        paddingTop: 0,
                        paddingBottom: 0,
                        width: '5.35rem'
                      }
                    )
                  }}
                  onMouseEnter={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, true)}
                  onMouseLeave={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, false)}
                  selected={isItemSelected}
                >
                  <TableCell
                    component="td"
                    scope="row"
                    sx={{ fontWeight: 600, marginLeft: '-10px' }}
                    align="left"
                    key={'label' + index}
                  >
                    {measurement.label}
                  </TableCell>
                  <StyledTableCell
                    className="limited-width light-fill"
                    component="td"
                    scope="row"
                    key={'reset' + index}
                  >
                    <UpdatableResetIconButton
                      descriptor={measurement.descriptor}
                      controller={mainController}
                      attachmentNumber={WamDescriptor.Points}
                      key={'resetBtn' + index}
                    />
                  </StyledTableCell>
                  <TableCell
                    component="td"
                    scope="row"
                    align="right"
                    key={'visibility' + index}
                    className="limited-width"
                  >
                    {measurement.beingEdited !== undefined && (
                      <PenCheckboxButton
                        onChange={(checked): void => {
                          mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                        }}
                        checked={measurement.beingEdited}
                        key={'beingEditedBtn' + index}
                      />
                    )}
                    <EyeCheckboxButton
                      onChange={(checked): void =>
                        mainController.setMeasurementVisibility(measurement.descriptor, checked)
                      }
                      checked={measurement.visible}
                      key={'visibilityBtn' + index}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Table aria-label="a dense table" size="small" key={'wideareaTableEntries' + index}>
              <TableBody
                key={'wideareaEntries' + index}
                sx={{
                  '& td:first-child svg': {
                    fontSize: '1em',
                    marginRight: '0.3em',
                    opacity: 0.7
                  }
                }}
              >
                {config.map((item: ISplineItemConfig, i: number) => {
                  const style: CSSProperties | undefined = getSplineItemStyleConfig(item.splineItem, theme);

                  return (
                    <TableRow
                      key={'tableRow' + i}
                      onMouseEnter={(): void => splineDetails.setHighlightedSplineItem(item.splineItem)}
                      onMouseLeave={(): void => splineDetails.setHighlightedSplineItem(SplineItems.None)}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '& td:first-child svg': {
                          fontSize: '1em',
                          marginRight: '0.3em'
                        },
                        ...getConditionalStyles(splineDetails.highlightedSplineItem === item.splineItem, theme)
                      }}
                    >
                      <TableCell align="left" key={item.labelKey + i} style={style}>
                        {item.label}
                      </TableCell>
                      <StyledTableCell className="limited-width light-fill cell-height" align="center">
                        {(item.splineItem === SplineItems.ApDistance || item.splineItem === SplineItems.SlDistance) && (
                          <UpdatableResetIconButton
                            title={'Reset orientation'}
                            attachmentNumber={WamDescriptor.ApSlDistanceOrientation}
                            descriptor={measurement.descriptor}
                            controller={mainController}
                          />
                        )}
                      </StyledTableCell>
                      <TableCell align="right" key={item.measurementKey + i} style={style}>
                        <UpdatableValue
                          descriptor={measurement.descriptor}
                          constructor={DT.AreaMeasurement}
                          valueKey={item.valueKey}
                        />{' '}
                        {item.unitOfMeasurement}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </React.Fragment>
        );
      })}
    </TableContainer>
  );
});
